import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Clapperboard,
  Globe,
  Loader2,
  Lock,
  Megaphone,
  Radio,
  Search,
  X,
} from 'lucide-react';
import { AdAccount } from '../../api/ads/ad-account.api';
import {
  BrandCampaignVisibility,
  BrandCreatorCard,
  getBrandCreatorDirectory,
} from '../../api/payment/brand-deal.api';
import {
  BrandLiveRateCard,
  getBrandLiveRateCard,
} from '../../api/payment/brand-live.api';
import BrandBookingDialog from '../brand-live/brand-booking-dialog';

/** Form tạo chiến dịch có sẵn trong BrandCampaignSection. */
export type CampaignFormMode = 'create' | 'create-brief' | 'create-proposal';

type Step = 'type' | 'craft' | 'live' | 'visibility';

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

/** Giá livestream của creator, hiện trên từng dòng khi chọn creator cho quảng cáo livestream. */
export const livePriceLabel = (c: BrandCreatorCard): string =>
  [
    c.liveMonthlyPriceVnd && c.liveMonthlyHours
      ? `Gói tháng ${fmtVnd(c.liveMonthlyPriceVnd)} cho ${
          c.liveMonthlyHours
        } giờ`
      : '',
    c.liveHourlyPriceVnd ? `${fmtVnd(c.liveHourlyPriceVnd)}/giờ` : '',
  ]
    .filter(Boolean)
    .join(' · ');

const TypeCard: React.FC<{
  Icon: typeof Megaphone;
  title: string;
  text: string;
  onClick: () => void;
}> = ({ Icon, title, text, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-start gap-3 rounded-xl border ui-divider p-4 text-left hover:border-[#0057FF] hover:bg-[#0057FF]/5"
  >
    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
      <Icon size={20} />
    </span>
    <span className="min-w-0">
      <span className="block font-semibold ui-text">{title}</span>
      <span className="block text-sm ui-muted">{text}</span>
    </span>
  </button>
);

/**
 * Một nút "Tạo chiến dịch" cho người thuê: chọn loại việc rồi mới mở form tương ứng (yêu cầu sếp 16/10). Trước đây
 * là 3 nút rời (chiến dịch / brief / đề xuất) và quảng cáo livestream không có lối vào ở trang Thuê creator.
 */
const CampaignTypePicker: React.FC<{
  bizAccounts: AdAccount[];
  onClose: () => void;
  onPickForm: (
    mode: CampaignFormMode,
    visibility: BrandCampaignVisibility
  ) => void;
  onGoToRatingsTab?: () => void;
}> = ({ bizAccounts, onClose, onPickForm, onGoToRatingsTab }) => {
  const [step, setStep] = useState<Step>('type');
  // Bước "Ai được làm chiến dịch này?": nhớ form sẽ mở và bước vừa đi qua để "Quay lại" về đúng chỗ.
  const [pending, setPending] = useState<{
    mode: CampaignFormMode;
    from: Step;
  } | null>(null);

  const askVisibility = (mode: CampaignFormMode, from: Step) => {
    setPending({ mode, from });
    setStep('visibility');
  };

  const goBack = () => {
    setStep(step === 'visibility' && pending ? pending.from : 'type');
  };
  const [q, setQ] = useState('');
  const [creators, setCreators] = useState<BrandCreatorCard[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [bookTarget, setBookTarget] = useState<{
    card: BrandCreatorCard;
    rate: BrandLiveRateCard;
  } | null>(null);

  const accountId = bizAccounts.find((a) => a.status === 'Verified')?.id;

  useEffect(() => {
    if (step !== 'live') return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      getBrandCreatorDirectory({
        q: q.trim() || undefined,
        adAccountId: accountId,
        liveOnly: true,
      })
        .then((r) => alive && setCreators(r.items))
        .catch(() => alive && setCreators([]))
        .finally(() => alive && setLoading(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [step, q, accountId]);

  const pickCreator = async (card: BrandCreatorCard) => {
    try {
      const rate = await getBrandLiveRateCard(card.userId);
      if (!rate) {
        toast.error('Creator này chưa nhận quảng cáo livestream.');
        return;
      }
      setBookTarget({ card, rate });
    } catch {
      toast.error('Không tải được bảng giá của creator, thử lại sau.');
    }
  };

  if (bookTarget) {
    return (
      <BrandBookingDialog
        creatorId={bookTarget.card.userId}
        creatorName={bookTarget.card.displayName}
        rateCard={bookTarget.rate}
        bizAccounts={bizAccounts}
        liveFirst
        onClose={() => setBookTarget(null)}
        onCreated={() => {
          setBookTarget(null);
          onClose();
        }}
        onGoToRatingsTab={onGoToRatingsTab}
      />
    );
  }

  const title =
    step === 'type'
      ? 'Bạn muốn thuê creator làm gì?'
      : step === 'craft'
      ? 'Gia công sản phẩm'
      : step === 'visibility'
      ? 'Ai được làm chiến dịch này?'
      : 'Chọn creator livestream';

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label={title}
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col gap-3 overflow-y-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-slate-800">
        <div className="flex items-center gap-2">
          {step !== 'type' && (
            <button
              type="button"
              aria-label="Quay lại"
              onClick={goBack}
              className="ui-muted hover:ui-text"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h2 className="flex-1 text-lg font-semibold ui-text">{title}</h2>
          <button
            type="button"
            aria-label="Đóng"
            onClick={onClose}
            className="ui-muted hover:ui-text"
          >
            <X size={18} />
          </button>
        </div>

        {step === 'type' && (
          <div className="space-y-2">
            <TypeCard
              Icon={Megaphone}
              title="Đăng bài"
              text="Creator đăng bài quảng cáo theo nội dung bạn đưa sẵn, giữ bài trên trang của họ một số ngày."
              onClick={() => askVisibility('create', 'type')}
            />
            <TypeCard
              Icon={Clapperboard}
              title="Gia công sản phẩm"
              text="Creator làm nội dung mới cho bạn: ảnh, video, bài viết. Bạn duyệt hoặc yêu cầu sửa trước khi đăng."
              onClick={() => setStep('craft')}
            />
            <TypeCard
              Icon={Radio}
              title="Quảng cáo livestream"
              text="Gắn thẻ quảng cáo của bạn lên livestream của creator, trả theo số giờ hiển thị."
              onClick={() => setStep('live')}
            />
          </div>
        )}

        {step === 'craft' && (
          <div className="space-y-2">
            <TypeCard
              Icon={Clapperboard}
              title="Tôi tự chọn creator và giao brief"
              text="Viết yêu cầu, mời creator bạn muốn hoặc đăng lên sàn cho creator nhận làm."
              onClick={() => askVisibility('create-brief', 'craft')}
            />
            <TypeCard
              Icon={Clapperboard}
              title="Để creator gửi ý tưởng, tôi chọn bài ưng ý"
              text="Nêu mong muốn, nhiều creator gửi bài mẫu, bạn chọn bài hợp nhất."
              onClick={() => onPickForm('create-proposal', 'Public')}
            />
          </div>
        )}

        {step === 'visibility' && pending && (
          <div className="space-y-2">
            <TypeCard
              Icon={Globe}
              title="Công khai, đăng lên Sàn chiến dịch"
              text="Mọi creator xem và xin làm. Admin duyệt xong chiến dịch tự lên sàn."
              onClick={() => onPickForm(pending.mode, 'Public')}
            />
            <TypeCard
              Icon={Lock}
              title="Riêng tư, chỉ creator tôi mời"
              text="Chọn creator và giá từng người, lời mời gửi đi ngay."
              onClick={() => onPickForm(pending.mode, 'Private')}
            />
          </div>
        )}

        {step === 'live' && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 rounded-lg border ui-divider px-3 py-2">
              <Search size={16} className="ui-muted" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Tìm theo tên hoặc username"
                className="w-full bg-transparent text-sm outline-none ui-text"
              />
            </label>
            {loading && (
              <div className="flex items-center gap-2 text-sm ui-muted">
                <Loader2 size={14} className="animate-spin" /> Đang tải…
              </div>
            )}
            {!loading && creators?.length === 0 && (
              <p className="text-sm ui-muted">
                Chưa có creator nào đang nhận quảng cáo livestream.
              </p>
            )}
            {!loading &&
              creators?.map((c) => (
                <button
                  key={c.userId}
                  type="button"
                  onClick={() => pickCreator(c)}
                  className="flex w-full items-center gap-3 rounded-xl border ui-divider p-3 text-left hover:border-[#0057FF]"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-sm font-semibold dark:bg-slate-600">
                    {c.avatar ? (
                      <img
                        src={c.avatar}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : (
                      c.displayName.trim()[0]?.toUpperCase() || '?'
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium ui-text">
                      {c.displayName}
                    </span>
                    <span className="block truncate text-xs ui-muted">
                      @{c.userName} · {c.followers.toLocaleString('vi-VN')}{' '}
                      người theo dõi
                    </span>
                    <span className="block truncate text-xs ui-text">
                      {livePriceLabel(c)}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold text-[#0057FF]">
                    Đặt quảng cáo
                  </span>
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignTypePicker;
